'use client';

import { Box, Flex, Heading, Image, Popover, Portal, Text } from '@chakra-ui/react';
import { memo, useCallback, useMemo, useState } from 'react';
import { LuCheck, LuChevronDown } from 'react-icons/lu';

import SearchInput from '@components/common/SearchInput';
import AssistantSurface from '@features/assistant/AssistantSurface';
import { PREDICT_ASSISTANT_CONFIG } from '@features/assistant/modeSuggestions';
import { allPredictionMarkets } from '@features/prediction-markets/fakeMarketData';
import MarketCardGrid from '@features/prediction-markets/MarketCardGrid';
import TimeCategorySidebar, {
  TimeCategoryTopBar,
} from '@features/prediction-markets/TimeCategorySidebar';
import { TimeCategory, WeeklySubCategory } from '@features/prediction-markets/types';
import WeeklySubCategoryTabs from '@features/prediction-markets/WeeklySubCategoryTabs';
import { allCommodities } from 'helpers/fakeData';
import useControlledPopover from 'hooks/useControlledPopover';

interface CommodityPickerProps {
  selectedIds: Set<string>;
  onToggle: (commodityId: string) => void;
  onSelectAll: () => void;
}

const CommodityPicker = memo(({ selectedIds, onToggle, onSelectAll }: CommodityPickerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { open, onToggle: onPopoverToggle, triggerRef, contentRef } = useControlledPopover();

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const isAll = selectedIds.size === 0;

  const filteredCommodities = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const list = allCommodities.filter(
      (c) =>
        query === '' ||
        c.name.toLowerCase().includes(query) ||
        c.ticker.toLowerCase().includes(query),
    );
    if (!isAll) {
      list.sort((a, b) => {
        const aSelected = selectedIds.has(a.id) ? 0 : 1;
        const bSelected = selectedIds.has(b.id) ? 0 : 1;
        return aSelected - bSelected;
      });
    }
    return list;
  }, [searchQuery, selectedIds, isAll]);

  const displayLabel = useMemo(() => {
    if (isAll) {return 'All';}
    if (selectedIds.size === 1) {
      const commodity = allCommodities.find((c) => selectedIds.has(c.id));
      return commodity?.name ?? 'All';
    }
    return `${selectedIds.size} selected`;
  }, [isAll, selectedIds]);

  const displayImage = useMemo(() => {
    if (selectedIds.size !== 1) {return null;}
    return allCommodities.find((c) => selectedIds.has(c.id)) ?? null;
  }, [selectedIds]);

  return (
    <Popover.Root open={open} onOpenChange={onPopoverToggle} lazyMount>
      <Popover.Trigger asChild>
        <Flex ref={triggerRef} alignItems="center" gap={2} cursor="pointer">
          {displayImage != null && (
            <Image
              src={displayImage.image}
              alt={displayImage.name}
              boxSize="28px"
              borderRadius="full"
            />
          )}
          <Heading size="lg">{displayLabel}</Heading>
          <LuChevronDown size={20} />
        </Flex>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            ref={contentRef}
            borderRadius="xl"
            bgColor="surface.background"
            p={2}
            minW="240px"
            maxH="360px"
            display="flex"
            flexDirection="column"
          >
            <SearchInput
              query={searchQuery}
              setQuery={setSearchQuery}
              onClear={handleClearSearch}
              placeholder="Search commodities"
              size="sm"
              mb={1}
            />
            <Flex direction="column" overflowY="auto" flex={1} gap={0.5}>
              <PickerItem label="All" isSelected={isAll} onClick={onSelectAll} />
              {filteredCommodities.map((c) => (
                <PickerItem
                  key={c.id}
                  label={c.name}
                  ticker={c.ticker}
                  image={c.image}
                  isSelected={selectedIds.has(c.id)}
                  onClick={() => onToggle(c.id)}
                />
              ))}
            </Flex>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
});
CommodityPicker.displayName = 'CommodityPicker';

const PickerItem = memo(
  ({
    label,
    ticker,
    image,
    isSelected,
    onClick,
  }: {
    label: string;
    ticker?: string;
    image?: string;
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <Flex
      alignItems="center"
      gap={2}
      px={2}
      py={1}
      borderRadius="md"
      cursor="pointer"
      _hover={{ bg: 'surface.hover' }}
      onClick={onClick}
    >
      <Box w={4} h={4} flexShrink={0} display="flex" alignItems="center" justifyContent="center">
        {isSelected && <LuCheck size={14} />}
      </Box>
      {image != null && <Image src={image} alt={label} boxSize="20px" borderRadius="full" />}
      <Text fontWeight="medium" fontSize="xs">
        {label}
      </Text>
      {ticker != null && (
        <Text color="text.secondary" fontSize="2xs">
          {ticker}
        </Text>
      )}
    </Flex>
  ),
);
PickerItem.displayName = 'PickerItem';

const EMPTY_SET = new Set<string>();

const PredictionMarketPage = () => {
  const [timeCategory, setTimeCategory] = useState<TimeCategory>('all');
  const [weeklySubCategory, setWeeklySubCategory] = useState<WeeklySubCategory>('all');
  const [selectedCommodityIds, setSelectedCommodityIds] = useState<Set<string>>(EMPTY_SET);

  const handleTimeCategory = useCallback((cat: TimeCategory) => {
    setTimeCategory(cat);
    setWeeklySubCategory('all');
  }, []);

  const handleWeeklySubCategory = useCallback((sub: WeeklySubCategory) => {
    setWeeklySubCategory(sub);
  }, []);

  const handleToggleCommodity = useCallback((id: string) => {
    setSelectedCommodityIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedCommodityIds(EMPTY_SET);
  }, []);

  return (
    <AssistantSurface config={PREDICT_ASSISTANT_CONFIG}>
      {/* The market explorer keeps its own two-pane, internally-scrolling layout,
          so it gets a bounded height and sits below the assistant in the flow. */}
      <Flex direction="column" h="85vh" minH="500px" p={4}>
        <TimeCategoryTopBar selected={timeCategory} onSelect={handleTimeCategory} />
        <Flex direction="row" flex={1} overflow="hidden">
          <TimeCategorySidebar selected={timeCategory} onSelect={handleTimeCategory} />
          <Flex direction="column" flex={1} p={4} pt={3} gap={4} overflowY="auto">
            <Flex
              direction={{ base: 'column', md: 'row' }}
              alignItems={{ base: 'flex-start', md: 'center' }}
              gap={3}
            >
              {timeCategory === 'weekly' && (
                <WeeklySubCategoryTabs
                  selected={weeklySubCategory}
                  onSelect={handleWeeklySubCategory}
                />
              )}
              <Flex flex={1} justifyContent="flex-end">
                <CommodityPicker
                  selectedIds={selectedCommodityIds}
                  onToggle={handleToggleCommodity}
                  onSelectAll={handleSelectAll}
                />
              </Flex>
            </Flex>
            <MarketCardGrid
              markets={allPredictionMarkets}
              timeCategory={timeCategory}
              weeklySubCategory={weeklySubCategory}
              commodityIds={selectedCommodityIds}
            />
          </Flex>
        </Flex>
      </Flex>
    </AssistantSurface>
  );
};

export default memo(PredictionMarketPage);
